import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { BASE_COLORS } from "config/theme/constants";
import DynamicTable from "components/common/DynamicTable";
import TableHeader from "components/common/TableHeader";
import {
  ecolabItemsString,
  stringConfig,
} from "config/site.string";
import { Breadcrumb, Col, Divider, Drawer, Form, Tooltip } from "antd";
import {
  ActivityLogWrapper,
  ActivityWrapper,
  InfoWrapper,
} from "pages/protectedPages/ActivityLog/styles";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "redux/store";
import { config } from "config/site.config";
import { isMobile } from "react-device-detect";
// import { loadState } from "library/helpers/sessionStorage";
// import { getGivenPermissions } from "utils/utils";
import {
  EditOutlined,
  HomeOutlined,
  InfoCircleOutlined
} from "@ant-design/icons";
import CustomLoader from "components/common/Loader/CustomLoader";
import { fetchEcolabItemDetailsListRequest } from "redux/ecolabItemDetails/actions";
import EditEcolabItemDetails from "./EditEcolabItemDetails";
import dayjs from "dayjs";

export const TABLE_PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_PAGE_SIZE: 10,
  DEFAULT_SORT_FIELD: "id",
  DEFAULT_SORT_ORDER: "DESC",
};

const EcolabItems = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  //   const permissionsOfUser = loadState("actualPermissionsList");
  //   const { hasViewAccess, hasEditAccess, hasFullAccess }: any =
  //     getGivenPermissions(permissionsOfUser, "Ecolab Item");
  const { ecolabItemDetailsList, loading, totalCount} = useSelector(
    (state: RootState) => state.ecolabItemDetails
  );
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [pagination, setPagination] = useState({
    current: TABLE_PAGINATION.DEFAULT_PAGE,
    pageSize: TABLE_PAGINATION.DEFAULT_PAGE_SIZE,
    total: 0,
  });
  const [searchValue, setSearchValue] = useState("");
  const [sortField, setSortField] = useState(
    TABLE_PAGINATION.DEFAULT_SORT_FIELD
  );
  const [sortOrder, setSortOrder] = useState(
    TABLE_PAGINATION.DEFAULT_SORT_ORDER
  );
  const [ecolabItemDetailsListData, setEcolabItemListData] =
    useState(ecolabItemDetailsList);
  const [initialEcolabItemList, setInitialEcolabItemList] =
    useState(ecolabItemDetailsList);
  const initialLoadRef = useRef(true);
  const [isInitialized, setIsInitialized] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState<any>({
    open: false,
    mode: "",
  });
  const [refresh] = useState(false);
  const [form] = Form.useForm();

  // Updated function to get user name by userId using allUsers data
//   const getUserName = (userId: number) => {
//     if (!userId) return userId?.toString() || "";
//     // First check if it's the current user
//     if (userInfo && userInfo.userId === userId) {
//       return (
//         `${userInfo.firstName || ""} ${userInfo.lastName || ""}`.trim() ||
//         userId.toString()
//       );
//     }
//     // Then check in allUsers data
//     const user = allUsers.find((user: any) => user.userId === userId);
//     if (user) {
//       return (
//         `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
//         userId.toString()
//       );
//     }
//     return userId.toString();
//   };

  // Initial load
  useEffect(() => {
    if (initialLoadRef.current) {
      setIsInitialized(true);
    }
  }, []);

  useEffect(() => {
    if (isInitialized) {
      const defaultParams = {
        pageNumber: pagination.current,
        pageSize: pagination.pageSize,
        sortColumn: sortField,
        sortOrder,
        searchValue,
      };

      dispatch(
        fetchEcolabItemDetailsListRequest(config.getEcolabItemDetailsList, defaultParams)
      );
    }
  }, [isInitialized, refresh, dispatch]);

  // Update item list data when redux state changes
  useEffect(() => {
    setEcolabItemListData(ecolabItemDetailsList);
    setInitialEcolabItemList(ecolabItemDetailsList);
  }, [ecolabItemDetailsList]);

  // Update pagination total when totalCount changes
  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      total: totalCount,
    }));
  }, [totalCount]);

  const handleTableChange = (
    newPagination: any,
    _filters: any,
    sorter: any
  ) => {
    const { current, pageSize } = newPagination;
    const newSortField =
      sorter.field || sortField || TABLE_PAGINATION.DEFAULT_SORT_FIELD;
    const newSortOrder = sorter.field
      ? sorter.order
      : sortOrder || TABLE_PAGINATION.DEFAULT_SORT_ORDER;

    setPagination((prev) => ({
      ...prev,
      current,
      pageSize,
    }));
    setSortField(newSortField);
    setSortOrder(newSortOrder);

    // Dispatch API call with new pagination parameters
    dispatch(
      fetchEcolabItemDetailsListRequest(config.getEcolabItemDetailsList, {
        pageNumber: current,
        pageSize,
        sortColumn: newSortField,
        sortOrder: newSortOrder,
        searchValue,
      })
    );
  };

  const handleSearch = (value: string) => {
    dispatch(
      fetchEcolabItemDetailsListRequest(config.getEcolabItemDetailsList, {
        pageNumber: TABLE_PAGINATION.DEFAULT_PAGE,
        pageSize: pagination.pageSize,
        sortColumn: sortField,
        sortOrder,
        searchValue: value,
      })
    );
    setSearchValue(value);
    setPagination((prev) => ({
      ...prev,
      current: TABLE_PAGINATION.DEFAULT_PAGE,
    }));
  };

  const handleEdit = (record: any) => {
    const parseDate = (value?: string) => {
      if (!value) return null;
      const parsed = dayjs.utc(value).isValid() ? dayjs.utc(value) : null;
      return parsed?.isValid() ? parsed : null;
    };

    const normalizedRecord = record
      ? {
          ...record,
          scheduledStartDate: parseDate(record.scheduledStartDate),
        }
      : null;

    setSelectedRecord(normalizedRecord);
    setIsModalOpen({ open: true, mode: "EDIT" });
    form.setFieldsValue(normalizedRecord);
  };

  const openAddItem = (from?: string) => {
    setIsModalOpen({ open: true, mode: from });
  };

  const handleOk = () => {
    setIsModalOpen({ open: false, mode: "" });
  };

  const handleCancel = () => {
    setIsModalOpen({ open: false, mode: "" });
  };

  const columnsData = [
    {
      title: ecolabItemsString.description,
      dataIndex: "description",
      isSorter: true,
      width: "10%",
    },
    {
      title: ecolabItemsString.shortDescription,
      dataIndex: "shortDescription",
      isSorter: true,
      width: "10%",
    },
    {
      title: ecolabItemsString.length,
      dataIndex: "length",
      isSorter: true,
      width: "12%",
    },
    {
      title: ecolabItemsString.width,
      dataIndex: "width",
      isSorter: true,
      width: "12%",
    },
    {
      title: ecolabItemsString.height,
      dataIndex: "height",
      isSorter: true,
      width: "12%",
    },
    // {
    //   title: "Created By",
    //   dataIndex: "createdBy",
    //   isSorter: true,
    //   width: "8%",
    //   render: (userId: number) => getUserName(userId),
    // },
    // {
    //   title: "Updated By",
    //   dataIndex: "updatedBy",
    //   isSorter: true,
    //   width: "8%",
    //   render: (userId: number) => getUserName(userId),
    // },
  ];

  const popOverIconStyle = {
    color: BASE_COLORS.curiousBlue,
    marginRight: 5,
  };

  const columnAction = {
    title: stringConfig.actions,
    dataIndex: "",
    key: "x",
    width: "7%",
    render: (item: any) => (
      <EditOutlined
        style={{ cursor: "pointer", ...popOverIconStyle }}
        onClick={() => handleEdit(item)}
      />
    ),
  };

  return (
    <ActivityWrapper>
      <InfoWrapper>
        <Breadcrumb
          items={[
            {
              title: <HomeOutlined />,
            },
            {
              title: <span style={{ cursor: "pointer" }}>Settings</span>,
              onClick: () => navigate("/settings"),
            },
            {
              title: <span>{ecolabItemsString.customerLog}</span>,
            },
          ]}
        />
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Tooltip
            title="View and manage ecolab item details. This page provides an interface for adding and updating ecolab item details."
            placement="bottomRight"
            overlayInnerStyle={{ maxWidth: 300 }}
          >
            <InfoCircleOutlined style={{ cursor: "pointer" }} />
          </Tooltip>
        </div>
      </InfoWrapper>
      <Divider style={{ marginBottom: "10px", marginTop: "10px" }} />
      <ActivityLogWrapper>
        {loading && <CustomLoader />}
        <Col span={24} style={{ marginTop: 15 }} className="tableRow">
          <TableHeader
            showModal={() => openAddItem("ADD")}
            placeHolder={stringConfig.searchPlaceholder}
            buttonText={ecolabItemsString.addItem}
            inputWidth={isMobile ? "100%" : 180}
            leftHeaderText={ecolabItemsString.customerLog}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            onServerSearch={handleSearch}
            searchLoading={loading}
            initialData={initialEcolabItemList}
            columnsKey={columnsData.map(({ dataIndex }) => dataIndex)}
            // isShowButton={!hasViewAccess}
          />
          <DynamicTable
            columns={columnsData}
            columnActions={columnAction}
            data={ecolabItemDetailsListData}
            loading={loading}
            onChange={handleTableChange}
            isPagination={true}
            pagination={pagination}
            sortField={sortField}
            sortOrder={sortOrder}
            totalCounts={pagination.total}
            initialData={initialEcolabItemList}
            scrollXWidth={1350}
          />
          {isModalOpen.open && (
            <Drawer
              title={isModalOpen.mode === "EDIT" ? "Edit Item" : "Add Item"}
              onClose={() => handleCancel()}
              open={isModalOpen.open}
              className="add-user-drawer"
              destroyOnClose
              width={450}
            >
              <EditEcolabItemDetails
                form={form}
                handleOk={handleOk}
                isEdit={isModalOpen.mode === "EDIT"}
                handleCancel={handleCancel}
                selectedRecord={selectedRecord}
                onClose={(isSuccess) => {
                  if (isSuccess) {
                    setSearchValue("");
                    setPagination((prev) => ({
                      ...prev,
                      current: TABLE_PAGINATION.DEFAULT_PAGE,
                    }));
                    dispatch(
                      fetchEcolabItemDetailsListRequest(config.getEcolabItemDetailsList, {
                        pageNumber: pagination.current,
                        pageSize: pagination.pageSize,
                        sortColumn: sortField,
                        sortOrder,
                        searchValue: "",
                      })
                    );
                  }
                  setSelectedRecord(null);
                  form.resetFields();
                  setIsModalOpen({
                    open: false,
                    mode: "",
                  });
                }}
              />
            </Drawer>
          )}
        </Col>
      </ActivityLogWrapper>
    </ActivityWrapper>
  );
};

export default EcolabItems;
